// src/pages/ExportBooksPage.tsx
// エクスポート画面。登録した本・抜粋メモをCSVファイル（ZIPにまとめて）で書き出す。

import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppTokens } from '../context/ThemeModeContext';
import { requestExport } from '../utils/exportApi';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';

const ExportBooksPage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { idToken, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queuedEmail, setQueuedEmail] = useState<string | null>(null);

  // 未ログインならトップへ自動遷移。Cognitoセッション確認の非同期処理を待つため少し遅らせる。
  useEffect(() => {
    if (isLoggedIn) return;
    const t = window.setTimeout(() => {
      navigate('/', { replace: true });
    }, 1200);
    return () => window.clearTimeout(t);
  }, [isLoggedIn, navigate]);

  const cardSx = {
    border: `1px solid ${TOKENS.border}`,
    background: TOKENS.glass,
    borderRadius: '18px',
    p: 2.5,
  } as const;

  const handleExport = async () => {
    if (!idToken) return;
    setExporting(true);
    setError(null);
    setQueuedEmail(null);
    try {
      const res = await requestExport(idToken);
      setQueuedEmail(res.email);
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'エクスポートに失敗しました。');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ mt: 7, minHeight: 'calc(100vh - 56px)', background: TOKENS.bg, pb: 6 }}>
      <Box sx={{ maxWidth: 720, margin: 'auto', px: 2, py: { xs: 3, md: 4 } }}>
        <Button
          component={Link}
          to="/bibliography"
          sx={{ mb: 1.5, textTransform: 'none', color: TOKENS.textSub, pl: 0 }}
        >
          ← 書誌管理
        </Button>

        <Typography sx={{ fontSize: 22, fontWeight: 700, color: TOKENS.text, mb: 0.5 }}>エクスポート</Typography>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 3 }}>
          登録した本と抜粋・メモをCSVファイルで書き出し、ダウンロードリンクをメールでお送りします
        </Typography>

        <Box sx={cardSx}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: TOKENS.text, mb: 1.5 }}>
            登録本・抜粋メモをCSVで書き出す
          </Typography>

          <Box
            sx={{
              background: TOKENS.cardBg,
              border: `1px solid ${TOKENS.border}`,
              borderRadius: '12px',
              p: 2,
              mb: 2.5,
            }}
          >
            <Typography sx={{ fontSize: 12.5, color: TOKENS.text, lineHeight: 1.9 }}>
              登録した本の一覧と、抜粋・メモの一覧を、それぞれ別のCSVファイルにまとめてZIPファイルを作成します。
              準備ができ次第、ご登録のメールアドレスにダウンロードリンクをお送りします（リンクは7日間有効です）。
              他サービスへの移行や、Excel等でのご自身での管理にお使いください。
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.textSub, mt: 2, mb: 0.75 }}>
              登録本一覧.csv
            </Typography>
            <Typography sx={{ fontSize: 12, color: TOKENS.textSub, lineHeight: 1.9 }}>
              ISBN・本棚名・公開設定・登録日時・タイトル・著者名・出版社名・発行年
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: TOKENS.textSub, mt: 1.5, mb: 0.75 }}>
              抜粋メモ一覧.csv
            </Typography>
            <Typography sx={{ fontSize: 12, color: TOKENS.textSub, lineHeight: 1.9 }}>
              対象の本のISBN・タイトル・テキスト・ページ番号・公開設定・作成日
            </Typography>
          </Box>

          <Button
            fullWidth
            disabled={exporting}
            onClick={handleExport}
            startIcon={!exporting ? <FileDownloadIcon sx={{ fontSize: 18 }} /> : undefined}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 700,
              py: 1,
              color: TOKENS.bg,
              background: TOKENS.text,
              '&:hover': { background: TOKENS.textSub },
              '&.Mui-disabled': { color: TOKENS.bg, background: TOKENS.borderSoft },
            }}
          >
            {exporting ? <CircularProgress size={18} sx={{ color: TOKENS.bg }} /> : 'エクスポートする'}
          </Button>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {queuedEmail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2.5, borderTop: `1px solid ${TOKENS.border}`, pt: 2 }}>
              <MarkEmailReadIcon sx={{ color: '#2e7d32', fontSize: 20 }} />
              <Typography sx={{ fontSize: 13, color: TOKENS.text }}>
                エクスポートを開始しました。準備ができ次第 {queuedEmail} 宛にダウンロードリンクをお送りします。
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ExportBooksPage;
